import React, {useCallback, useEffect} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import Alert from "react-s-alert";
import {Button, styled, theme, Text} from "@reside/ui";
import {RoleNameEnum} from "../../utils/types/common";
import {TableUserDto} from "../../utils/types/user";
import {DocumentTitle} from "../../atoms/document-title";
import {AdminRoles} from "../../atoms/admin-roles";
import {Footer} from "../../atoms/footer";
import {ReportingLayout} from "./reporting-layout";
import {PERMISSIONS_MANAGEMENT_LIST_PATH} from "./atoms/permissions-user-detail";
import {
  BulkPermissionManagementForm,
  formatBulkPendingChangesLabel,
  PermissionsBulkEditHeader,
  useBulkUserPermissionsMutation,
} from "./atoms/permissions-bulk-edit";
import {useBulkQuicksightDashboards} from "./atoms/permissions-bulk-edit/hooks/useBulkQuicksightDashboards";

type LocationState = Readonly<{
  users?: ReadonlyArray<TableUserDto>;
}>;

export const PageReportingPermissionsBulkEdit = () => {
  const navigate = useNavigate();
  const location = useLocation<LocationState>();
  const users = location.state?.users ?? [];

  useEffect(() => {
    if (users.length === 0) {
      navigate(PERMISSIONS_MANAGEMENT_LIST_PATH, {replace: true});
    }
  }, [navigate, users.length]);

  const {
    discardChanges,
    hasPermission,
    isPermissionPending,
    isSaving,
    pendingChangeCount,
    save,
    syncQuicksightReporting,
    toggleCategory,
    togglePermission,
    userIds,
  } = useBulkUserPermissionsMutation(users);

  const {
    dashboardOptions,
    discardChanges: discardQuicksightChanges,
    hasDashboard,
    isDashboardPending,
    isDashboardsDataReady,
    isError: isQuicksightError,
    isLoading: isQuicksightLoading,
    isSaving: isQuicksightSaving,
    pendingChangeCount: quicksightPendingChangeCount,
    save: saveQuicksight,
    toggleAllDashboards,
    toggleDashboard,
  } = useBulkQuicksightDashboards({
    users,
    onDashboardsChange: syncQuicksightReporting,
  });

  const totalPendingChangeCount =
    pendingChangeCount + quicksightPendingChangeCount;
  const isSavingChanges = isSaving || isQuicksightSaving;

  const goBackToList = () => {
    navigate(PERMISSIONS_MANAGEMENT_LIST_PATH);
  };

  const handleCancel = () => {
    discardChanges();
    discardQuicksightChanges();
    goBackToList();
  };

  const hasPendingQuicksightChanges = quicksightPendingChangeCount > 0;
  const isQuicksightSaveBlocked =
    hasPendingQuicksightChanges && !isDashboardsDataReady;

  const handleSave = useCallback(async () => {
    if (totalPendingChangeCount === 0 || isQuicksightSaveBlocked) {
      return;
    }

    if (hasPendingQuicksightChanges) {
      try {
        await saveQuicksight();
      } catch {
        return;
      }
    }

    save({
      onSuccess: () => {
        if (pendingChangeCount === 0) {
          Alert.success("Permissions saved successfully.");
        }
      },
    });
  }, [
    hasPendingQuicksightChanges,
    isQuicksightSaveBlocked,
    pendingChangeCount,
    save,
    saveQuicksight,
    totalPendingChangeCount,
  ]);

  if (users.length === 0) {
    return null;
  }

  return (
    <ReportingLayout isActionRowVisible={false}>
      <DocumentTitle title="Reside - Admin - Bulk Permissions" />
      <AdminRoles
        roles={[RoleNameEnum.RESIDE_ADMIN, RoleNameEnum.ORGANIZATION_ADMIN]}
      >
        <PageContainer>
          <PermissionsBulkEditHeader users={users} />
          <FormSection>
            <BulkPermissionManagementForm
              users={users}
              userIds={userIds}
              hasPermission={hasPermission}
              isPermissionPending={isPermissionPending}
              onTogglePermission={togglePermission}
              onToggleCategory={toggleCategory}
              dashboardOptions={dashboardOptions}
              isQuicksightLoading={isQuicksightLoading}
              isQuicksightError={isQuicksightError}
              isQuicksightSaving={isQuicksightSaving}
              hasDashboard={hasDashboard}
              isDashboardPending={isDashboardPending}
              onToggleDashboard={toggleDashboard}
              onToggleAllDashboards={toggleAllDashboards}
            />
          </FormSection>
          <StickyFooter
            left={
              totalPendingChangeCount > 0 ? (
                <PendingChangesText>
                  {formatBulkPendingChangesLabel(
                    totalPendingChangeCount,
                    userIds.length,
                  )}
                </PendingChangesText>
              ) : null
            }
            right={
              <FooterButtons>
                <Button outline color="primary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={
                    isSavingChanges ||
                    totalPendingChangeCount === 0 ||
                    isQuicksightSaveBlocked
                  }
                  onClick={() => handleSave()}
                >
                  Save
                </Button>
              </FooterButtons>
            }
          />
        </PageContainer>
      </AdminRoles>
    </ReportingLayout>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 8px 0 0;
`;

const FormSection = styled.div`
  flex: 1;
  padding-bottom: 40px;
`;

const StickyFooter = styled(Footer)`
  align-items: center;
  background-color: #ffffff;
  box-shadow: 0 -12px 12px 0 ${theme.color.white},
    0 -12px 24px 0 ${theme.color.white};
  margin-top: 32px;
  padding: 16px 0;
  width: 100%;
  z-index: 2;

  @media (min-width: ${({theme}) => theme.breakpoint.md}px) {
    padding: 24px 0;
  }
`;

const PendingChangesText = styled(Text)`
  color: ${theme.color.darkBlue100};
  font-size: 15px;
  line-height: 24px;
  margin-left: 24px;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  padding-right: 80px;
`;
